import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  ViewEncapsulation,
  DestroyRef,
} from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import {
  MatCalendar,
  MatDatepickerIntl,
  yearsPerPage,
} from '@angular/material/datepicker';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { MONTH_KEYS, DAY_KEYS } from '../constants';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
/**
 * Custom calendar header
 */
@Component({
  selector: 'app-datepicker-header',
  standalone: true,
  imports: [MatButtonModule, MatIconButton, MatTooltipModule, TranslocoPipe],
  templateUrl: './datepicker-header.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class DatepickerHeaderComponent<D> {
  private _intl: MatDatepickerIntl = inject(MatDatepickerIntl);
  private _transloco = inject(TranslocoService);
  calendar = inject(MatCalendar<D>) as MatCalendar<D>;
  private _dateAdapter = inject(DateAdapter<D>, { optional: true })!;
  private _cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  _periodButtonText = '';
  _periodButtonDescription = '';
  _periodButtonLabel = '';
  _prevButtonLabel = '';
  _nextButtonLabel = '';
  _periodButtonLabelId = `omm-calendar-period-${Math.random().toString(36).slice(2)}`;

  constructor() {
    this._updateLabels();
    this.calendar.stateChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this._updateLabels();
        this._cdr.markForCheck();
      });
  }

  get periodButtonText(): string {
    return this._periodButtonText;
  }
  get periodButtonDescription(): string {
    return this._periodButtonDescription;
  }
  get periodButtonLabel(): string {
    return this._periodButtonLabel;
  }
  get prevButtonLabel(): string {
    return this._prevButtonLabel;
  }
  get nextButtonLabel(): string {
    return this._nextButtonLabel;
  }

  // Translation key for long day name
  get headerDayKey(): string {
    const date =
      (this.calendar.selected as D | null) ?? this.calendar.activeDate;
    const weekday = this._dateAdapter.getDayOfWeek(date);
    return `components.date-input.daysLong.${DAY_KEYS[weekday % 7]}`;
  }

  // Translation key for long month name
  get headerMonthKey(): string {
    const date =
      (this.calendar.selected as D | null) ?? this.calendar.activeDate;
    const month = this._dateAdapter.getMonth(date);
    return `components.date-input.monthsLong.${MONTH_KEYS[month]}`;
  }

  // Day of month number for the header bar
  get headerDayOfMonth(): number {
    const date =
      (this.calendar.selected as D | null) ?? this.calendar.activeDate;
    return this._dateAdapter.getDate(date);
  }

  // Year for the header bar
  get headerYear(): string {
    const date =
      (this.calendar.selected as D | null) ?? this.calendar.activeDate;
    return this._dateAdapter.getYearName(date);
  }

  currentPeriodClicked(): void {
    this.calendar.currentView =
      this.calendar.currentView === 'month' ? 'multi-year' : 'month';
  }

  previousClicked(): void {
    if (this.previousEnabled()) {
      this.calendar.activeDate =
        this.calendar.currentView === 'month'
          ? this._dateAdapter.addCalendarMonths(this.calendar.activeDate, -1)
          : this._dateAdapter.addCalendarYears(
              this.calendar.activeDate,
              this.calendar.currentView === 'year' ? -1 : -yearsPerPage
            );
    }
  }

  nextClicked(): void {
    if (this.nextEnabled()) {
      this.calendar.activeDate =
        this.calendar.currentView === 'month'
          ? this._dateAdapter.addCalendarMonths(this.calendar.activeDate, 1)
          : this._dateAdapter.addCalendarYears(
              this.calendar.activeDate,
              this.calendar.currentView === 'year' ? 1 : yearsPerPage
            );
    }
  }

  previousEnabled(): boolean {
    if (!this.calendar.minDate) return true;
    return !this._isSameView(this.calendar.activeDate, this.calendar.minDate);
  }

  nextEnabled(): boolean {
    if (!this.calendar.maxDate) return true;
    return !this._isSameView(this.calendar.activeDate, this.calendar.maxDate);
  }

  private _updateLabels(): void {
    const calendar = this.calendar;
    const intl = this._intl;
    const adapter = this._dateAdapter;
    const t = (key: string) => this._transloco.translate(key);
    if (calendar.currentView === 'month') {
      const shortMonths = adapter.getMonthNames('short');
      const monthLabel =
        shortMonths[adapter.getMonth(calendar.activeDate)] +
        ' ' +
        adapter.getYearName(calendar.activeDate);
      this._periodButtonText = monthLabel.toUpperCase();
      this._periodButtonDescription = this._periodButtonText;
      this._periodButtonLabel = t('components.date-input.header.switchToMultiYearView');
      this._prevButtonLabel = t('components.date-input.header.prevMonth');
      this._nextButtonLabel = t('components.date-input.header.nextMonth');
    } else if (calendar.currentView === 'year') {
      this._periodButtonText = adapter.getYearName(calendar.activeDate);
      this._periodButtonDescription = this._periodButtonText;
      this._periodButtonLabel = t('components.date-input.header.switchToMonthView');
      this._prevButtonLabel = t('components.date-input.header.prevYear');
      this._nextButtonLabel = t('components.date-input.header.nextYear');
    } else {
      this._periodButtonText = intl.formatYearRange(
        ...this._formatMinAndMaxYearLabels()
      );
      this._periodButtonDescription = intl.formatYearRangeLabel(
        ...this._formatMinAndMaxYearLabels()
      );
      this._periodButtonLabel = t('components.date-input.header.switchToMonthView');
      this._prevButtonLabel = t('components.date-input.header.prevMultiYear');
      this._nextButtonLabel = t('components.date-input.header.nextMultiYear');
    }
    this._cdr.markForCheck();
  }

  private _isSameView(date1: D, date2: D): boolean {
    if (this.calendar.currentView === 'month') {
      return (
        this._dateAdapter.getYear(date1) === this._dateAdapter.getYear(date2) &&
        this._dateAdapter.getMonth(date1) === this._dateAdapter.getMonth(date2)
      );
    }
    if (this.calendar.currentView === 'year') {
      return (
        this._dateAdapter.getYear(date1) === this._dateAdapter.getYear(date2)
      );
    }
    const year1 = this._dateAdapter.getYear(date1);
    const year2 = this._dateAdapter.getYear(date2);
    const startingYear = this._getStartingYear(
      this.calendar.minDate,
      this.calendar.maxDate
    );
    return (
      Math.floor((year1 - startingYear) / yearsPerPage) ===
      Math.floor((year2 - startingYear) / yearsPerPage)
    );
  }

  private _formatMinAndMaxYearLabels(): [string, string] {
    const activeYear = this._dateAdapter.getYear(this.calendar.activeDate);
    const minYearOfPage =
      activeYear -
      this._getActiveOffset(
        this.calendar.activeDate,
        this.calendar.minDate,
        this.calendar.maxDate
      );
    const maxYearOfPage = minYearOfPage + yearsPerPage - 1;
    const minYearLabel = this._dateAdapter.getYearName(
      this._dateAdapter.createDate(minYearOfPage, 0, 1)
    );
    const maxYearLabel = this._dateAdapter.getYearName(
      this._dateAdapter.createDate(maxYearOfPage, 0, 1)
    );
    return [minYearLabel, maxYearLabel];
  }

  private _getActiveOffset(
    activeDate: D,
    minDate: D | null,
    maxDate: D | null
  ): number {
    const activeYear = this._dateAdapter.getYear(activeDate);
    return this._euclideanModulo(
      activeYear - this._getStartingYear(minDate, maxDate),
      yearsPerPage
    );
  }

  private _getStartingYear(minDate: D | null, maxDate: D | null): number {
    if (maxDate) {
      return this._dateAdapter.getYear(maxDate) - yearsPerPage + 1;
    }
    if (minDate) {
      return this._dateAdapter.getYear(minDate);
    }
    return 0;
  }

  private _euclideanModulo(a: number, b: number): number {
    return ((a % b) + b) % b;
  }
}
